import React, { useState } from 'react';
import type { ConsultationEnrollmentViewProps } from 'interfaces/enrollments/ConsultationEnrollmentViewProps';
import type { ConsultationSubmission } from 'models/submissions/ConsultationSubmission';

const ConsultationEnrollmentView: React.FC<ConsultationEnrollmentViewProps> = ({
  enrollment,
  onSubmit,
  isDisabled = false,
}) => {
  const [dateTime, setDateTime] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');

  const isPhysical = enrollment.consultationType === 'Physical';
  const isOnline = enrollment.consultationType === 'Online';

  const canSubmit = dateTime.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;

    const appointmentTimeUtc = new Date(dateTime).toISOString();

    const submission: ConsultationSubmission = {
      id: '',
      enrollmentId: enrollment.id,
      nodeInstanceId: enrollment.nodeInstanceId ?? '',
      workflowInstanceId: '',
      submittedByUserId: '',
      type: 'ConsultationSubmission',
      submittedAt: new Date().toISOString(),
      appointmentTimeUtc,
      confirmedAddress: isPhysical ? enrollment.consultationAddress : undefined,
      meetingUrl: isOnline && meetingUrl.trim() ? meetingUrl.trim() : undefined,
    };

    onSubmit(submission);
  };

  const typeLabel = isPhysical ? 'In-Person' : isOnline ? 'Online' : enrollment.consultationType;

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Schedule Your Consultation</h2>

      {/* Consultation type badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Consultation Type:</span>
        <span
          className={`inline-block rounded-full px-3 py-0.5 text-xs font-semibold ${
            isPhysical
              ? 'bg-orange-100 text-orange-700'
              : 'bg-blue-100 text-blue-700'
          }`}
        >
          {typeLabel}
        </span>
      </div>

      {/* Physical branch */}
      {isPhysical && enrollment.consultationAddress && (
        <div>
          <span className="text-sm font-medium text-gray-700">Location: </span>
          <span className="text-sm text-gray-600">{enrollment.consultationAddress}</span>
        </div>
      )}

      {/* Online branch */}
      {isOnline && enrollment.videoProvider && (
        <div>
          <span className="text-sm font-medium text-gray-700">Video Provider: </span>
          <span className="text-sm text-gray-600">{enrollment.videoProvider}</span>
        </div>
      )}

      {/* Date-time picker */}
      <div>
        <label
          htmlFor="consultation-datetime"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Proposed Date &amp; Time
        </label>
        <input
          id="consultation-datetime"
          type="datetime-local"
          value={dateTime}
          onChange={(e) => setDateTime(e.target.value)}
          disabled={isDisabled}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Meeting URL — Online only */}
      {isOnline && (
        <div>
          <label
            htmlFor="consultation-meeting-url"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Paste your meeting link{' '}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="consultation-meeting-url"
            type="url"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            disabled={isDisabled}
            placeholder="https://meet.google.com/..."
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isDisabled || !canSubmit}
        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Confirm Appointment
      </button>
    </div>
  );
};

export default ConsultationEnrollmentView;
